import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Input
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import ShowLoader from "@/components/Loader";
import Toaster from "@/components/Toaster";
import { fetchCountry, fetchCity } from "@/store/slices/internalUsersSlice";
import { fetchDeliveryPartners, assignOrderToDeliveryPartner, fetchOrderPayments } from "@/store/slices/orderSlice";
import { useTranslation } from "react-i18next";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "50px", // Taller fields like reference image
        borderColor: state.isFocused ? "#212121" : "#CFD2D9",
        boxShadow: state.isFocused ? "0 0 0 1px #212121" : "none",
        "&:hover": { borderColor: "#CFD2D9" },
    }),
    singleValue: (provided) => ({ ...provided, color: "#111827", fontSize: "14px" }),
    placeholder: (provided) => ({ ...provided, color: "#9CA3AF", fontSize: "14px" }),
    menu: (provided) => ({ ...provided, zIndex: 50 }),
};

const priorities = [
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
];

const maxTenDigits = (val) => !val || String(val).length <= 10;
const decimalRegex = /^\d+(\.\d{1,2})?$/;

const schema = (maxQty, order_pending_amount, idEdit, initialAssignedTo, t) =>

    yup.object().shape({
        country_id: yup.string().required(t('country_is_required')),
        city_id: yup.string().required(t('city_is_required')),
        delivery_agent_id: yup.string().required(t('delivery_agent_required')),
        deadline: idEdit
            ? yup.string().nullable().notRequired()
            : yup.string().required(t('deadline_date_required')),
        priority: yup.string().required(t('priority_required')),
        delivery_address: yup.string().trim().required(t('delivery_location_is_required'))
            .min(3, t('delivery_location_min_length'))
            .max(150, t('delivery_location_max_length')),
        delivery_landmark: yup
            .string()
            .trim()
            .required(t('landmark_is_required')),
        latitude: yup
            .mixed()
            .test(
                "has-latitude",
                t('select_location_for_coordinates'),
                (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))
            ),
        longitude: yup
            .mixed()
            .test(
                "has-longitude",
                t('select_location_for_coordinates'),
                (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))
            ),
        notes: yup.string().required(t('note_is_required')).max(150, t('max_150_characters')),
        full_name: yup.string().trim().required(t('recipient_name_is_required')).max(100, t('max_100_characters')),
        country_code: yup.string().trim().required(t('country_code_is_required')),
        mobile_number: yup
            .string()
            .trim()
            .required(t('mobile_number_is_required'))
            .test("max-10-digits", t('mobile_number_max_digits'), maxTenDigits)
            .matches(/^\d+$/, t('mobile_number_digits_only')),
        amount_deducted_reason: yup.string().nullable().notRequired().max(150, t('max_150_characters')),
        // amount_deducted: yup
        //     .number()
        //     .transform((value, originalValue) =>
        //         originalValue === "" || originalValue == null ? undefined : value
        //     )
        //     .nullable()
        //     .notRequired()
        //     .min(0, t('cannot_be_negative')),
        amount_deducted: yup
            .number()
            .transform((value, originalValue) =>
                originalValue === "" || originalValue == null ? undefined : value
            )
            .nullable()
            .notRequired()
            .min(0, t('cannot_be_negative'))
            .test(
                "max-decimals",
                t('max_2_decimal_places'),
                (val) => val === undefined || val === null || decimalRegex.test(String(val))
            )
            .test(
                "less-than-delivery-charge-paid-amount",
                t('deducted_amount_cannot_be_more_than_paid'),
                function (value) {
                    const { delivery_charge_paid_amount } = this.parent;
                    // If either value is missing, skip this validation (let .required() handle it if necessary)
                    if (value === undefined || value === null || delivery_charge_paid_amount === undefined || delivery_charge_paid_amount === null) {
                        return true;
                    }
                    return value <= delivery_charge_paid_amount;
                }
            ),
        delivery_charge: yup
            .number()
            .typeError(t('delivery_charge_must_be_a_number'))
            .required(t('delivery_charge_is_required'))
            .test("max-decimals", t('max_2_decimal_places'), (val) => val === undefined || decimalRegex.test(String(val)))
            .min(0, t('delivery_charge_cannot_be_negative')),
        delivery_charge_paid_amount: yup
            .number()
            .transform((value, originalValue) =>
                originalValue === "" || originalValue == null ? undefined : value
            )
            .nullable()
            .notRequired()
            .min(0, t('delivery_charge_cannot_be_negative'))
            .test("max-decimals", t('max_2_decimal_places'), (val) => val === undefined || decimalRegex.test(String(val)))
            .test(
                "less-than-delivery-and-installation-charge",
                t('paid_amount_less_than_delivery_charge'),
                function (value) {
                    const { delivery_charge, installation_charge } = this.parent;

                    if (value === undefined || value === null) {
                        return true;
                    }

                    const delivery = Number(delivery_charge) || 0;
                    const installation = Number(installation_charge) || 0;
                    const totalMaxAllowed = delivery + installation;

                    return Number(value) <= totalMaxAllowed;
                }
            ),

        installation_charge: yup
            .number()
            .typeError(t('installation_charge_must_be_a_number'))
            .required(t('installation_charge_is_required'))
            .test(
                "max-decimals",
                t('max_2_decimal_places'),
                (val) => val === undefined || decimalRegex.test(String(val))
            )
            .min(0, t('installation_charge_cannot_be_negative')),
        cash_amount: yup
            .number()
            .typeError(t('amount_must_be_a_number'))
            .required(t('amount_to_be_collected_is_required'))
            .min(0, t('cannot_be_negative'))
            .max(order_pending_amount, t('max_amount', { amount: order_pending_amount }))
            .test("max-decimals", t('max_2_decimal_places'), (val) => val === undefined || decimalRegex.test(String(val))),
        quantity: yup
            .number()
            .typeError(t('qty_must_be_a_number'))
            .required(t('required_field'))
            .min(1, t('min_1'))
            .max(maxQty, t('max_qty', { maxQty })),
        description: yup.string().when("delivery_agent_id", {
            is: (value) =>
                idEdit &&
                String(value) !== String(initialAssignedTo),

            then: (schema) =>
                schema
                    .required(t('reason_required_on_change'))
                    .min(5, t('min_5_chars'))
                    .max(255, t('max_255_chars')),

            otherwise: (schema) =>
                schema.nullable().notRequired(),
        }),
    });

export default function AssignDeliveryAgentModal({ onClose, item, rfq_product_id, vendor, initialData = null }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');
    const maxQty = vendor.product_quantity || 1;
    const vendorCountryId = initialData?.location?.country_id ?? "";
    const vendorCityId = initialData?.location?.city_id ?? "";

    const countries = useSelector((state) => state.internalUser?.countries || []);
    const cities = useSelector((state) => state.internalUser?.cities || []);
    const mobile_country_code = useSelector((state) => state.internalUser?.mobile_country_code || []);
    const { delivery_partners = [], loading: orderLoading, order_payments } = useSelector((state) => state.order);
    const { loading: userLoading } = useSelector((state) => state.internalUsers || {});

    // const { order_payments } = useSelector((state) => state.order);

    const isLoading = orderLoading || userLoading;

    const formatDateTimeLocal = (date) => {
        if (!date) return "";

        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, "0");

        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            country_id: initialData?.location?.country_id,
            city_id: initialData?.location?.city_id,
            delivery_agent_id: initialData?.assigned_to,
            quantity: initialData?.quantity ?? maxQty,
            amount_deducted_reason: initialData?.amount_deducted_reason,
            amount_deducted: initialData?.amount_deducted,
            delivery_charge: initialData?.delivery_charge ?? null,
            cash_amount: initialData?.cash_amount ?? item?.payment_summary?.due_amount ?? null,
            deadline: initialData?.deadline
                ? formatDateTimeLocal(initialData.deadline)
                : null,
            priority: initialData?.priority ?? "medium",
            delivery_address:
                initialData?.delivery_address ?? "",
            installation_charge:
                initialData?.installation_charge ??
                ((Number(item?.installation_charge) || 0) * Number(initialData?.quantity ?? maxQty)),
            delivery_landmark:
                initialData?.delivery_landmark ?? "",
            latitude:
                initialData?.latitude ?? null,
            longitude:
                initialData?.longitude ?? null,
            notes: initialData?.notes ?? null,
            delivery_charge_paid_amount: initialData?.delivery_charge_paid_amount ?? null,
            full_name:
                initialData?.recipient_full_name ??
                initialData?.full_name ??
                "",

            country_code:
                initialData?.recipient_country_code ??
                initialData?.country_code ??
                "+966",

            mobile_number:
                initialData?.recipient_mobile_number ??
                initialData?.mobile_number ??
                "",
            description: "",
        },
        // resolver: yupResolver(schema(maxQty, item?.payment_summary?.due_amount, initialData?.assigned_to)),
        resolver: yupResolver(
            schema(
                maxQty,
                item?.payment_summary?.due_amount,
                Boolean(initialData),
                initialData?.assigned_to,
                t
            )
        ),
    });

    useEffect(() => {
        if (!initialData && item?.payment_summary?.due_amount) {
            setValue("cash_amount", item.payment_summary.due_amount, { shouldValidate: true });
        }
    }, [item, initialData, setValue]);

    // Keep your previous API prefill logic as a fallback if needed
    useEffect(() => {
        // Only override if we don't have an initial item due_amount and API returns data
        if (!initialData && !item?.payment_summary?.due_amount && order_payments?.data?.summary) {
            const amount = order_payments.data.summary.amount_pending;
            setValue("cash_amount", Number(amount), { shouldValidate: true });
        }
    }, [order_payments, setValue, initialData, item]);

    const selectedCountry = watch("country_id");
    const selectedCity = watch("city_id");
    const formValues = watch();

    const selectedDeliveryPartner = watch("delivery_agent_id");

    const isDeliveryPartnerChanged =
        initialData &&
        String(selectedDeliveryPartner) !== String(initialData?.assigned_to);

    const isEdit = Boolean(initialData);

    const isPartnerLocked =
        initialData &&
        initialData?.collected_quantity !== null &&
        initialData?.collected_quantity !== undefined;

    const [showInfo, setShowInfo] = useState({ name: true, phone: true, email: true });

    useEffect(() => {
        dispatch(fetchCountry());
        // dispatch(fetchOrderPayments({ order_id: item?.rfq_id }));
    }, [dispatch]);

    useEffect(() => {
        if (initialData) {
            reset({
                country_id: initialData?.location?.country_id,
                city_id: initialData?.location?.city_id,
                delivery_agent_id: initialData?.assigned_to,
                quantity: maxQty,
                amount_deducted_reason: initialData?.amount_deducted_reason,
                amount_deducted: initialData?.amount_deducted,
                delivery_charge: initialData?.delivery_charge ?? null,
                cash_amount: initialData?.cash_amount ?? null,
                deadline: initialData?.deadline
                    ? formatDateTimeLocal(initialData.deadline)
                    : null,
                priority: initialData?.priority ?? "medium",
                delivery_address:
                    initialData?.delivery_address ?? "",
                installation_charge: initialData?.installation_charge ?? item?.installation_charge ?? 0,
                delivery_landmark:
                    initialData?.delivery_landmark ?? "",
                latitude:
                    initialData?.latitude ?? null,
                longitude:
                    initialData?.longitude ?? null,
                notes: initialData?.notes ?? null,
                delivery_charge_paid_amount: initialData?.delivery_charge_paid_amount ?? null,
                full_name:
                    initialData?.recipient_full_name ??
                    initialData?.full_name ??
                    "",

                country_code:
                    initialData?.recipient_country_code ??
                    initialData?.country_code ??
                    "+966",

                mobile_number:
                    initialData?.recipient_mobile_number ??
                    initialData?.mobile_number ??
                    "",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, reset, maxQty]);

    // Auto-select location from vendor (when opening modal)
    useEffect(() => {
        if (vendorCountryId) {
            setValue("country_id", String(vendorCountryId), { shouldValidate: true });
        }
    }, [vendorCountryId, setValue]);

    useEffect(() => {
        if (selectedCountry) {
            dispatch(fetchCity(selectedCountry));
            // keep vendor city pre-selection if available; otherwise reset
            if (!vendorCityId) {
                setValue("city_id", "");
            }
        }
    }, [selectedCountry, dispatch, setValue]);

    // Once city list is loaded, preselect vendor city (if present)
    useEffect(() => {
        if (!vendorCityId) return;
        if (!selectedCountry) return;
        if (!cities?.length) return;

        const cityValue = String(vendorCityId);
        const exists = cities.some((c) => String(c.value) === cityValue);
        if (exists) {
            setValue("city_id", cityValue, { shouldValidate: true });
        }
    }, [vendorCityId, selectedCountry, cities, setValue]);

    useEffect(() => {
        if (selectedCountry && selectedCity) {
            dispatch(fetchDeliveryPartners({ country_id: selectedCountry, city_id: selectedCity }));
        }
    }, [selectedCountry, selectedCity, dispatch]);

    const onSubmit = async (formData) => {
        try {
            const payload = {
                id: initialData?.id ?? null,
                order_id: Number(item?.id),
                assigned_to: Number(formData.delivery_agent_id),
                rfq_product_id: rfq_product_id,
                vendor_id: vendor?.vendor_id,
                quantity: Number(formData.quantity),
                delivery_charge: Number(formData.delivery_charge),
                cash_amount: Number(formData.cash_amount),
                // deadline: formData.deadline?.split("T")[0],
                deadline: formData.deadline,
                priority: formData.priority,
                notes: formData.notes,
                delivery_address: String(formData.delivery_address || "").trim(),
                delivery_landmark: String(formData.delivery_landmark || "").trim(),
                description: formData.description,
                latitude: Number(formData.latitude),
                longitude: Number(formData.longitude),
                is_name_visible: showInfo.name ? "yes" : "no",
                is_mobile_number_visible: showInfo.phone ? "yes" : "no",
                is_email_visible: showInfo.email ? "yes" : "no",
                installation_charge: Number(formData.installation_charge),
                full_name: String(formData.full_name || "").trim(),
                country_code: String(formData.country_code || "").trim(),
                mobile_number: Number(formData.mobile_number),
                ...(formData.amount_deducted_reason
                    ? { amount_deducted_reason: formData.amount_deducted_reason }
                    : {}),
                ...(formData.amount_deducted !== "" && formData.amount_deducted != null
                    ? { amount_deducted: Number(formData.amount_deducted) }
                    : {}),
                ...(formData.delivery_charge_paid_amount !== "" &&
                    formData.delivery_charge_paid_amount != null
                    ? {
                        delivery_charge_paid_amount: Number(
                            formData.delivery_charge_paid_amount
                        ),
                    }
                    : {}),
            };

            await dispatch(assignOrderToDeliveryPartner(payload)).unwrap();
            Toaster.success(t('delivery_agents_assigned_success'));
            onClose();
        } catch (err) { Toaster.error(err?.message || t('assignment_failed')); }
    };

    const inputLabelClass = "mb-2 block text-sm font-semibold text-[#1F2937]";
    const commonInputClass = "!border !border-[#CFD2D9] rounded-lg h-[50px] !text-[#111827] focus:!border-[#212121] transition";

    const now = new Date().toISOString().slice(0, 16);

    const onError = (err) => {
        console.log(err);
    }

    useEffect(() => {
        if (initialData) return;

        const customerAddress = item?.rfq?.customer_delivery_address;
        const latitude = item?.rfq?.latitude;
        const longitude = item?.rfq?.longitude;

        if (customerAddress) {
            setValue("delivery_address", customerAddress, {
                shouldValidate: true,
            });

            setValue("delivery_landmark", customerAddress, {
                shouldValidate: true,
            });
        }

        if (latitude !== null && latitude !== undefined && latitude !== "") {
            setValue("latitude", Number(latitude), {
                shouldValidate: true,
            });
        }

        if (longitude !== null && longitude !== undefined && longitude !== "") {
            setValue("longitude", Number(longitude), {
                shouldValidate: true,
            });
        }
    }, [item, initialData, setValue]);

    return (
        <Dialog open={true} handler={onClose} size="sm" className="rounded-2xl p-0 overflow-hidden shadow-2xl 2xl:min-w-[40%] 2xl:max-w-[40%]">
            {isLoading && <ShowLoader />}
            <DialogHeader className="flex justify-between items-center border-b px-8 py-5 bg-white z-10 relative">
                <Typography className="text-xl font-bold text-gray-900">
                    {t('assign_delivery_agent_title')}
                </Typography>
                <XMarkIcon className="h-6 w-6 cursor-pointer text-gray-500 hover:text-black transition" onClick={onClose} />
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit, onError)}>
                {/* This invisible input will take the initial focus instead of the deadline field */}
                <input type="text" style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }} autoFocus />
                <DialogBody className="p-0 max-h-[70vh] overflow-y-auto">
                    {/* Padding added here to matches the design spacing */}
                    <div className="px-8 pt-6 pb-2 space-y-6">
                        {/* Vendor & Quantity header (requested) */}
                        <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        {t('vendor_header')}
                                    </p>
                                    <p className="text-base font-bold text-gray-900 truncate">
                                        {vendor?.vendor_name || "N/A"}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        {t('quantity_header')}
                                    </p>
                                    <p className="text-base font-bold text-gray-900">
                                        {maxQty}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        {t('order_pending_amount_label')}
                                    </p>
                                    <p className="text-base font-bold text-gray-900">
                                        {item?.payment_summary?.currency_code} {item?.payment_summary?.due_amount}
                                    </p>
                                </div>

                            </div>
                        </div>

                        {/* Location Selection Block - No grid, stacked */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="relative">
                                <label className={inputLabelClass}>{t('select_country_label')}</label>
                                <Controller
                                    control={control}
                                    name="country_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder={t('country_placeholder')}
                                            options={countries}
                                            value={countries.find(c => String(c.value) === String(field.value)) || null}
                                            onChange={(val) => field.onChange(val?.value != null ? String(val.value) : "")}
                                            styles={customSelectStyles}
                                            // isDisabled={initialData?.delivery_timeline === 'delivered' || initialData?.status === 'approved'}
                                            isDisabled={initialData?.delivery_timeline === 'delivered' || isPartnerLocked}
                                        />
                                    )}
                                />
                                {errors.country_id && <p className="text-red-600 text-xs mt-1.5">{errors.country_id.message}</p>}
                            </div>
                            <div className="relative">
                                <label className={inputLabelClass}>{t('select_city_label')}</label>
                                <Controller
                                    control={control}
                                    name="city_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isDisabled={!selectedCountry || (initialData?.delivery_timeline === 'delivered' || isPartnerLocked)}
                                            // isDisabled={!selectedCountry || (initialData?.delivery_timeline === 'delivered'|| initialData?.status === 'approved')}
                                            placeholder={t('city_placeholder')}
                                            options={cities}
                                            value={cities.find(c => String(c.value) === String(field.value)) || null}
                                            onChange={(val) => field.onChange(val?.value != null ? String(val.value) : "")}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />
                                {errors.city_id && <p className="text-red-600 text-xs mt-1.5">{errors.city_id.message}</p>}
                            </div>
                        </div>

                        {/* Single Agent form (no add-more) */}
                        <div className="space-y-5">
                            <div>
                                <label className={inputLabelClass}>{t('select_delivery_agent_label')}</label>
                                <Controller
                                    control={control}
                                    name="delivery_agent_id"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            // isDisabled={!selectedCity || (initialData?.delivery_timeline === 'delivered' || initialData?.status === 'approved')}
                                            isDisabled={!selectedCity || initialData?.delivery_timeline === 'delivered' || isPartnerLocked}
                                            placeholder={t('choose_delivery_agent_placeholder')}
                                            options={delivery_partners}
                                            value={delivery_partners.find(dp => String(dp.value) === String(field.value)) || null}
                                            onChange={(val) => field.onChange(val?.value != null ? String(val.value) : "")}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />
                                {errors.delivery_agent_id && <p className="text-red-600 text-xs mt-1.5">{errors.delivery_agent_id.message}</p>}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={inputLabelClass}>{t('deadline_date_label')}</label>
                                    <Input
                                        type="datetime-local"
                                        min={now}
                                        {...register("deadline")}
                                        className={commonInputClass}
                                        containerProps={{
                                            className: "min-w-[100px] h-[50px]",
                                        }}
                                        labelProps={{ className: "hidden" }}

                                    />
                                    {errors.deadline && <p className="text-red-600 text-xs mt-1.5">{errors.deadline.message}</p>}
                                </div>
                                <div>
                                    <label className={inputLabelClass}>{t('priority_label')}</label>
                                    <Controller
                                        control={control}
                                        name="priority"
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={priorities}
                                                value={priorities.find(p => p.value === field.value)}
                                                onChange={(val) => field.onChange(val?.value)}
                                                styles={customSelectStyles}
                                            />
                                        )}
                                    />
                                    {errors.priority && <p className="text-red-600 text-xs mt-1.5">{errors.priority.message}</p>}
                                </div>
                            </div>

                            <div>
                                <label className={inputLabelClass}>{t('delivery_location_label')}</label>
                                {/* <Input
                                    type="text"
                                    {...register("delivery_address")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder="Enter full pickup address (street, area, city)"
                                /> */}
                                {/* {errors.delivery_address && (
                                    <p className="text-red-600 text-xs mt-1.5">
                                        {errors.delivery_address.message}
                                    </p>
                                )} */}
                                <Controller
                                    control={control}
                                    name="delivery_address"
                                    render={({ field }) => (
                                        <DeliveryLocationSearch
                                            value={field.value}
                                            onChange={({
                                                delivery_address,
                                            }) => {
                                                setValue(
                                                    "delivery_address",
                                                    delivery_address ?? "",
                                                    { shouldValidate: true }
                                                );
                                            }}
                                            placeholder={t('search_area_street_placeholder')}
                                            error={
                                                errors.delivery_address?.message ? errors.delivery_address?.message : ''
                                            }
                                        />
                                    )}
                                />
                            </div>

                            <div>
                                <label className={inputLabelClass}>
                                    {t('delivery_landmark_label')}
                                </label>
                                <Controller
                                    control={control}
                                    name="delivery_landmark"
                                    render={({ field }) => (
                                        <DeliveryLocationSearch
                                            value={field.value}
                                            latitude={formValues?.latitude}
                                            longitude={formValues?.longitude}
                                            onChange={({
                                                delivery_landmark,
                                                latitude,
                                                longitude,
                                            }) => {
                                                setValue(
                                                    "delivery_landmark",
                                                    delivery_landmark ?? "",
                                                    { shouldValidate: true }
                                                );
                                                setValue(
                                                    "latitude",
                                                    latitude != null ? Number(latitude) : null,
                                                    { shouldValidate: true }
                                                );
                                                setValue(
                                                    "longitude",
                                                    longitude != null ? Number(longitude) : null,
                                                    { shouldValidate: true }
                                                );
                                            }}
                                            placeholder={t('search_landmark_placeholder')}
                                            error={
                                                errors.delivery_landmark?.message ? errors.delivery_landmark?.message :
                                                    (errors.latitude?.message ||
                                                        errors.longitude?.message) ? t('please_enter_valid_address') : ''
                                            }
                                        />
                                    )}
                                />
                            </div>



                            <div>
                                <label className={inputLabelClass}>{t('note_label')}</label>
                                <textarea
                                    rows="3"
                                    maxLength={150}
                                    className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 focus:border-[#212121] outline-none text-sm transition"
                                    {...register("notes")}
                                    placeholder={t('enter_notes_for_agent_placeholder')}
                                />
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.notes?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {(formValues?.notes || "").length}/150
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className={inputLabelClass}>{t('recipient_full_name_label')}</label>
                                <Input
                                    type="text"
                                    {...register("full_name")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_recipient_full_name_placeholder')}
                                    maxLength={100}
                                />
                                {errors.full_name && (
                                    <p className="text-red-600 text-xs mt-1.5">{errors.full_name.message}</p>
                                )}
                            </div>

                            {/* <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-6 w-full">
                                <div className="w-full sm:w-auto shrink-0">
                                    <label className={inputLabelClass}>{t('country_code_label')}</label>
                                    <Input
                                        type="text"
                                        {...register("country_code")}
                                        className={commonInputClass}
                                        labelProps={{ className: "hidden" }}
                                        placeholder={t('country_code_placeholder')}
                                    />
                                    {errors.country_code && (
                                        <p className="text-red-600 text-xs mt-1.5">{errors.country_code.message}</p>
                                    )}
                                </div>
                                <div className="w-full sm:w-2/3 min-w-[200px]">
                                    <label className={inputLabelClass}>{t('recipient_mobile_number_label')}</label>
                                    <Input
                                        type="text"
                                        {...register("mobile_number")}
                                        className={commonInputClass}
                                        placeholder={t('enter_mobile_number_placeholder')}
                                        labelProps={{ className: "hidden" }}
                                        onInput={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, "");
                                            if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                        }}
                                    />
                                    {errors.mobile_number && (
                                        <p className="text-red-600 text-xs mt-1.5">{errors.mobile_number.message}</p>
                                    )}
                                </div>
                            </div> */}

                            {/* Updated Recipient Mobile Number & Country Code Layout */}
                            <div className="w-full">
                                <label className={inputLabelClass}>{t('recipient_mobile_number_label')}</label>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                                    <div className="md:col-span-2">
                                        <Controller
                                            control={control}
                                            name="country_code"
                                            render={({ field }) => (
                                                <Select
                                                    placeholder={t('select')}
                                                    options={mobile_country_code}
                                                    value={mobile_country_code?.find(opt => opt.value === field.value) || null}
                                                    onChange={(code) => field.onChange(code?.value ?? "")}
                                                    isClearable
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '8px 0 0 8px', // flush with input box
                                                            borderColor: errors.country_code ? '#DC2626' : '#CFD2D9',
                                                        }),
                                                    }}
                                                    components={{
                                                        Option: CountryOption,
                                                        SingleValue: CountrySingleValue
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="md:col-span-4">
                                        <Input
                                            type="text"
                                            {...register("mobile_number")}
                                            className="!border !border-[#CFD2D9] rounded-r-lg rounded-l-none h-[50px] !text-[#111827] focus:!border-[#212121] transition"
                                            placeholder={t('enter_mobile_number_placeholder')}
                                            labelProps={{ className: "hidden" }}
                                            onInput={(e) => {
                                                e.target.value = e.target.value.replace(/\D/g, "");
                                                if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                            }}
                                        />
                                    </div>
                                </div>
                                {(errors.mobile_number || errors.country_code) && (
                                    <p className="text-red-600 text-xs mt-1.5">
                                        {errors.mobile_number?.message || errors.country_code?.message}
                                    </p>
                                )}
                            </div>

                            {isDeliveryPartnerChanged && (
                                <div>
                                    <label className={inputLabelClass}>
                                        {t('reassignment_reason_label')}
                                    </label>

                                    <textarea
                                        rows="3"
                                        maxLength={255}
                                        className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 focus:border-[#212121] outline-none text-sm transition"
                                        {...register("description")}
                                        placeholder={t('enter_reassignment_reason_placeholder')}
                                    />

                                    <div className="flex items-center justify-between mt-1">
                                        <span className="text-red-600 text-xs">
                                            {errors.description?.message || ""}
                                        </span>

                                        <span className="text-xs text-gray-500">
                                            {(formValues?.description || "").length}/255
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className={inputLabelClass}>{t('amount_to_be_collected_label')}</label>
                                <div className="flex items-stretch w-full">
                                <span className="inline-flex items-center px-3 bg-[#F3F4F6] text-[#4B5563] text-sm font-medium border border-r-0 border-[#CBD5E1] rounded-l-md select-none">
                                    {item?.payment_summary?.currency_code}
                                </span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    {...register("cash_amount")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_collected_amount_placeholder')}
                                    onInput={(e) => {
                                        if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                    }}
                                />
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.cash_amount?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {String(formValues?.cash_amount ?? "").length}/10
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-semibold text-[#1F2937]">
                                        {t('installation_charge_label')}
                                    </label>

                                    {/* <span className="text-xs text-gray-600">
                                        {t('product_installation_charge_per_item')}: {item?.installation_charge ?? "0.00"}
                                    </span> */}
                                </div>
                                <div className="flex items-stretch w-full">
                                <span className="inline-flex items-center px-3 bg-[#F3F4F6] text-[#4B5563] text-sm font-medium border border-r-0 border-[#CBD5E1] rounded-l-md select-none">
                                    {item?.payment_summary?.currency_code}
                                </span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    {...register("installation_charge")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_installation_charge_placeholder')}
                                    onInput={(e) => {
                                        if (e.target.value.length > 10)
                                            e.target.value = e.target.value.slice(0, 10);
                                    }}
                                />
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.installation_charge?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {String(formValues?.installation_charge ?? "").length}/10
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className={inputLabelClass}>{t('delivery_charge_label')}</label>
                                <div className="flex items-stretch w-full">
                                <span className="inline-flex items-center px-3 bg-[#F3F4F6] text-[#4B5563] text-sm font-medium border border-r-0 border-[#CBD5E1] rounded-l-md select-none">
                                    {item?.payment_summary?.currency_code}
                                </span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    {...register("delivery_charge")}
                                    className={commonInputClass}
                                    labelProps={{ className: "hidden" }}
                                    placeholder={t('enter_delivery_charge_placeholder')}
                                    onInput={(e) => {
                                        if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                                    }}
                                />
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-red-600 text-xs">
                                        {errors.delivery_charge?.message || ""}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {String(formValues?.delivery_charge ?? "").length}/10
                                    </span>
                                </div>
                            </div>


                            {isEdit && (
                                <>
                                    <div>
                                        <label className={inputLabelClass}>
                                            {t('amount_paid_to_dp_label')}
                                        </label>
                                        <div className="flex items-stretch w-full">
                                        <span className="inline-flex items-center px-3 bg-[#F3F4F6] text-[#4B5563] text-sm font-medium border border-r-0 border-[#CBD5E1] rounded-l-md select-none">
                                            {item?.payment_summary?.currency_code}
                                        </span>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min={0}
                                            {...register("delivery_charge_paid_amount")}
                                            className={commonInputClass}
                                            labelProps={{ className: "hidden" }}
                                            placeholder={t('enter_amount_paid_placeholder')}
                                        />
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                            <span className="text-red-600 text-xs">
                                                {errors.delivery_charge_paid_amount?.message || ""}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {String(formValues?.delivery_charge_paid_amount ?? "").length}/10
                                            </span>
                                        </div>
                                    </div>
                                    {initialData?.delivery_charge_paid_amount &&
                                        <>
                                            <div>
                                                <label className={inputLabelClass}>{t('deducted_amount_label')}</label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    min={0}
                                                    {...register("amount_deducted")}
                                                    className={commonInputClass}
                                                    labelProps={{ className: "hidden" }}
                                                    placeholder={t('enter_deducted_amount_placeholder')}
                                                />
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-red-600 text-xs">
                                                        {errors.amount_deducted?.message || ""}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {String(formValues?.amount_deducted ?? "").length}/10
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className={inputLabelClass}>{t('deducted_amount_reason_label')}</label>
                                                <textarea
                                                    rows="3"
                                                    maxLength={150}
                                                    className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 focus:border-[#212121] outline-none text-sm transition"
                                                    {...register("amount_deducted_reason")}
                                                    placeholder={t('enter_deducted_amount_reason_placeholder')}
                                                />
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-red-600 text-xs">
                                                        {errors.amount_deducted_reason?.message || ""}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {(formValues?.amount_deducted_reason || "").length}/150
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    }
                                </>
                            )}


                        </div>

                        {/* item?.customer Info Section from design */}
                        <div className="p-1 pb-4">
                            <Typography className="text-sm font-semibold mb-4 text-[#1F2937]">
                                {t('choose_data_to_show_partner')}
                            </Typography>
                            <div className="space-y-3.5 text-sm">
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">{t('name_label')}: <span className="font-semibold text-[#111827]">
                                        {(() => {
                                            const fName = item?.customer?.first_name || "";
                                            const lName = item?.customer?.last_name || "";
                                            const full = (fName + " " + lName).trim();
                                            return full || "N/A";
                                        })()}
                                    </span>
                                    </span>
                                    <input type="checkbox" className="h-5 w-5 accent-black cursor-pointer rounded" checked={showInfo.name} onChange={() => setShowInfo({ ...showInfo, name: !showInfo.name })} />
                                </div>
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">{t('phone_number_label')}: <span className="font-semibold text-[#111827]">{item?.customer ? `${item?.customer.country_code}${item?.customer.mobile_number}` : "N/A"}</span></span>
                                    <input type="checkbox" className="h-5 w-5 accent-black cursor-pointer rounded" checked={showInfo.phone} onChange={() => setShowInfo({ ...showInfo, phone: !showInfo.phone })} />
                                </div>
                                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-[#374151]">{t('email_label')}: <span className="font-semibold text-[#111827]">{item?.customer?.email || "N/A"}</span></span>
                                    <input type="checkbox" className="h-5 w-5 accent-black cursor-pointer rounded" checked={showInfo.email} onChange={() => setShowInfo({ ...showInfo, email: !showInfo.email })} />
                                </div>
                            </div>
                        </div>

                        {/* Assign Quantity (read-only like mobile) */}
                        <div className="pb-6">
                            <label className={inputLabelClass}>{t('assign_quantity_label', { maxQty })}</label>
                            <Input
                                type="number"
                                min={0}
                                readOnly
                                value={maxQty}
                                className={`${commonInputClass} bg-gray-100 cursor-not-allowed`}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="border-t flex justify-end gap-3.5 px-8 py-5 bg-white z-10 relative">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-lg bg-[#F3F4F6] text-[#374151] font-medium hover:bg-[#E5E7EB] transition-colors">{t('not_now_button')}</button>
                    <button type="submit" className="px-9 py-2.5 rounded-lg bg-[#F2AD25] text-[#111827] font-bold hover:brightness-95 shadow-md transition-all"> {initialData ? t('update_now_button') : t('assign_now_button')}</button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}